'use client';

import { c as _c } from "react/compiler-runtime";
import type { ApprovalMode } from '@/lib/code-sessions/approval-mode';
import type { CodeSessionSpeed } from '@/lib/code-sessions/models';
import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import type { PreparedFork, SavedSessionTitle } from './runtime/types';
import type { CodeFormAnswer, CodePromptSubmission, CodeSessionRow, CodeSessionSummary, SessionResponse } from './types';
import { codeSessionActionDescription, withCodeSessionToast } from './action-toast';
import { seedNewSession } from './create-session';
import { codeSessionKeys } from './queries';
import { codeSessionRequest, jsonBody } from './runtime/cloud';
import { isPermissionReply } from './runtime/local/approvals';
import { retryToast, SessionFailure, UnconfirmedPromptError } from './runtime/request-error';
import { runtimeFor } from './runtime/runtime-for';
import { listsFor } from './runtime/session-lists';
import { getCodeSessionController } from './session-controller';
import { turnDisplayText, turnPromptText } from './turn-prompt';
import { fixSessionKeys } from '@/data/fix-sessions/queries';
import { forkedCodeSessionTitle } from '@/lib/code-sessions/title';
import { isLiveTurnStatus } from '@/lib/code-sessions/turn-utils';
import { useAuth } from '@/providers/auth-provider';

/**
 * A send that failed with a failure its runtime worded for the person (`SessionFailure`), as
 * `failure`. The mutation reports it with one toast, with Retry where one can mend it, and the
 * composer takes the text back for the person to send again.
 */
export class ReportedPromptError extends Error {
  constructor(readonly failure: SessionFailure) {
    super(failure.message, {
      cause: failure
    });
  }
}

/**
 * What an answer's toasts say. A permission answer has its failure's toast alone: the card
 * clears and the command runs, which shows the answer went through, and a toast while it is sent
 * and after it would sit over the composer's Send button.
 */
function answerToasts(formId: string, answer: CodeFormAnswer): {
  loading?: string;
  success?: string;
  error: string;
} {
  if (isPermissionReply(formId, answer)) {
    return {
      error: 'Could not send answer'
    };
  }
  if ('cancel' in answer) {
    return {
      loading: 'Dismissing question…',
      success: 'Question dismissed',
      error: 'Could not dismiss question'
    };
  }
  return {
    loading: 'Sending answer…',
    success: 'Answer sent',
    error: 'Could not send answer'
  };
}
export function useCodeSessionMutations(sessionId) {
  const $ = _c(119);
  const client = useQueryClient();
  let t0;
  if ($[0] !== client || $[1] !== sessionId) {
    t0 = getCodeSessionController(client, sessionId);
    $[0] = client;
    $[1] = sessionId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const controller = t0;
  let t1;
  if ($[3] !== client || $[4] !== controller || $[5] !== sessionId) {
    t1 = turnId => {
      const turn = client.getQueryData(codeSessionKeys.detail(sessionId))?.session.turns.find(saved => saved.id === turnId);
      const pending = controller.getSnapshot().pendingPrompts.find(prompt => prompt.turnId === turnId);
      return codeSessionActionDescription(client, sessionId, turn ? turnDisplayText(turn) : pending?.text);
    };
    $[3] = client;
    $[4] = controller;
    $[5] = sessionId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const turnDescription = t1;
  let t2;
  if ($[7] !== controller || $[8] !== sessionId) {
    t2 = error => retryToast(error, () => runtimeFor(sessionId).retry(() => controller.refresh({
      cancelRefetch: true
    })));
    $[7] = controller;
    $[8] = sessionId;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const toastRetry = t2;
  let t3;
  if ($[10] !== sessionId) {
    t3 = codeSessionKeys.mutation(sessionId, "prompt");
    $[10] = sessionId;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  let t5;
  if ($[12] !== controller) {
    t4 = async input => {
      ;
      try {
        return await controller.conversation.prompt(input);
      } catch (t6) {
        const error_0 = t6;
        throw error_0 instanceof SessionFailure ? new ReportedPromptError(error_0) : error_0;
      }
    };
    t5 = t7 => {
      const {
        turnId: turnId_0,
        ...submission
      } = t7;
      return controller.beginPrompt(turnId_0, submission);
    };
    $[12] = controller;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  let t6;
  if ($[15] !== client || $[16] !== controller) {
    t6 = t7 => {
      const {
        turn: turn_0,
        cutIn
      } = t7;
      controller.saveTurn(turn_0);
      client.invalidateQueries({
        queryKey: fixSessionKeys.list
      });
      if (!cutIn) {
        controller.setCuttingIn(turn_0.id, false);
      }
    };
    $[15] = client;
    $[16] = controller;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== controller || $[19] !== toastRetry) {
    t7 = (error_1, input_0) => {
      controller.rejectPrompt(input_0.turnId, error_1 instanceof UnconfirmedPromptError);
      if (error_1 instanceof UnconfirmedPromptError) {
        controller.refresh({
          cancelRefetch: true
        });
      }
      if (error_1 instanceof ReportedPromptError) {
        toast.error("Could not send message", {
          id: `code-session-prompt:${input_0.turnId}`,
          description: error_1.message,
          ...toastRetry(error_1.failure)
        });
      }
    };
    $[18] = controller;
    $[19] = toastRetry;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== controller) {
    t8 = (_data, error_2, t9) => {
      const {
        turnId: turnId_1
      } = t9;
      return controller.finishPrompt(turnId_1, error_2);
    };
    $[21] = controller;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t3 || $[24] !== t4 || $[25] !== t5 || $[26] !== t6 || $[27] !== t7 || $[28] !== t8) {
    t9 = {
      mutationKey: t3,
      retry: false,
      mutationFn: t4,
      onMutate: t5,
      onSuccess: t6,
      onError: t7,
      onSettled: t8
    };
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const prompt_0 = useMutation(t9);
  let t10;
  if ($[30] !== sessionId) {
    t10 = codeSessionKeys.mutation(sessionId, "cancel");
    $[30] = sessionId;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== controller) {
    t11 = async turnId_2 => {
      await controller.waitForPrompt(turnId_2);
      return controller.conversation.cancelTurn(turnId_2);
    };
    $[32] = controller;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== toastRetry || $[35] !== turnDescription) {
    t12 = (error_3, turnId_3) => {
      toast.error("Could not remove queued message", {
        description: `${turnDescription(turnId_3)}\n${error_3.message}`,
        descriptionClassName: "whitespace-pre-line",
        ...toastRetry(error_3)
      });
    };
    $[34] = toastRetry;
    $[35] = turnDescription;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  let t14;
  let t15;
  if ($[37] !== controller) {
    t13 = turnId_4 => controller.setCancelling(turnId_4, true);
    t14 = t16 => {
      const {
        turn: turn_1
      } = t16;
      return controller.saveTurn(turn_1);
    };
    t15 = (_data_0, _error, turnId_5) => {
      controller.setCancelling(turnId_5, false);
      controller.refresh();
    };
    $[37] = controller;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
  } else {
    t13 = $[38];
    t14 = $[39];
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t10 || $[42] !== t11 || $[43] !== t12 || $[44] !== t13 || $[45] !== t14 || $[46] !== t15) {
    t16 = {
      mutationKey: t10,
      retry: false,
      mutationFn: t11,
      onError: t12,
      onMutate: t13,
      onSuccess: t14,
      onSettled: t15
    };
    $[41] = t10;
    $[42] = t11;
    $[43] = t12;
    $[44] = t13;
    $[45] = t14;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  const cancel = useMutation(t16);
  let t17;
  if ($[48] !== sessionId) {
    t17 = codeSessionKeys.mutation(sessionId, "steer");
    $[48] = sessionId;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  let t19;
  if ($[50] !== controller) {
    t18 = async turnId_6 => {
      await controller.waitForPrompt(turnId_6);
      return controller.conversation.steerTurn(turnId_6);
    };
    t19 = t20 => {
      const {
        turn: turn_2
      } = t20;
      return controller.saveTurn(turn_2);
    };
    $[50] = controller;
    $[51] = t18;
    $[52] = t19;
  } else {
    t18 = $[51];
    t19 = $[52];
  }
  let t20;
  if ($[53] !== controller || $[54] !== toastRetry || $[55] !== turnDescription) {
    t20 = (error_4, turnId_7) => {
      toast.error("Could not send queued message", {
        description: `${turnDescription(turnId_7)}\n${error_4.message}`,
        descriptionClassName: "whitespace-pre-line",
        ...toastRetry(error_4)
      });
      controller.refresh();
    };
    $[53] = controller;
    $[54] = toastRetry;
    $[55] = turnDescription;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== t17 || $[58] !== t18 || $[59] !== t19 || $[60] !== t20) {
    t21 = {
      mutationKey: t17,
      retry: false,
      mutationFn: t18,
      onSuccess: t19,
      onError: t20
    };
    $[57] = t17;
    $[58] = t18;
    $[59] = t19;
    $[60] = t20;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  const steer = useMutation(t21);
  let t22;
  if ($[62] !== sessionId) {
    t22 = codeSessionKeys.mutation(sessionId, "send-now");
    $[62] = sessionId;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  let t24;
  let t25;
  if ($[64] !== controller) {
    t23 = async turnId_8 => {
      try {
        await controller.waitForPrompt(turnId_8);
      } catch {
        return {
          interrupted: false
        };
      }
      return controller.conversation.sendNow();
    };
    t24 = turnId_9 => controller.setCuttingIn(turnId_9, true);
    t25 = (t26, turnId_10) => {
      const {
        interrupted
      } = t26;
      if (!interrupted) {
        controller.setCuttingIn(turnId_10, false);
      }
    };
    $[64] = controller;
    $[65] = t23;
    $[66] = t24;
    $[67] = t25;
  } else {
    t23 = $[65];
    t24 = $[66];
    t25 = $[67];
  }
  let t26;
  if ($[68] !== controller || $[69] !== toastRetry || $[70] !== turnDescription) {
    t26 = (error_5, turnId_11) => {
      controller.setCuttingIn(turnId_11, false);
      toast.error("Could not send now", {
        description: `${turnDescription(turnId_11)}\n${error_5.message}`,
        descriptionClassName: "whitespace-pre-line",
        ...toastRetry(error_5)
      });
      controller.refresh();
    };
    $[68] = controller;
    $[69] = toastRetry;
    $[70] = turnDescription;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== t22 || $[73] !== t23 || $[74] !== t24 || $[75] !== t25 || $[76] !== t26) {
    t27 = {
      mutationKey: t22,
      retry: false,
      mutationFn: t23,
      onMutate: t24,
      onSuccess: t25,
      onError: t26
    };
    $[72] = t22;
    $[73] = t23;
    $[74] = t24;
    $[75] = t25;
    $[76] = t26;
    $[77] = t27;
  } else {
    t27 = $[77];
  }
  const sendNow = useMutation(t27);
  let t28;
  if ($[78] !== sessionId) {
    t28 = codeSessionKeys.mutation(sessionId, "interrupt");
    $[78] = sessionId;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  let t29;
  let t30;
  let t31;
  if ($[80] !== controller) {
    t29 = _turnIds => controller.conversation.interrupt();
    t30 = turnIds => controller.beginInterrupt(turnIds);
    t31 = () => {
      controller.refresh({
        cancelRefetch: true
      });
    };
    $[80] = controller;
    $[81] = t29;
    $[82] = t30;
    $[83] = t31;
  } else {
    t29 = $[81];
    t30 = $[82];
    t31 = $[83];
  }
  let t32;
  if ($[84] !== client || $[85] !== controller || $[86] !== sessionId || $[87] !== toastRetry) {
    t32 = (error_6, _turnIds_0, requestId) => {
      if (requestId) {
        controller.rejectInterrupt(requestId);
      }
      toast.error("Could not interrupt turn", {
        description: `${codeSessionActionDescription(client, sessionId)}\n${error_6.message}`,
        descriptionClassName: "whitespace-pre-line",
        ...toastRetry(error_6)
      });
      controller.refresh();
    };
    $[84] = client;
    $[85] = controller;
    $[86] = sessionId;
    $[87] = toastRetry;
    $[88] = t32;
  } else {
    t32 = $[88];
  }
  let t33;
  if ($[89] !== t28 || $[90] !== t29 || $[91] !== t30 || $[92] !== t31 || $[93] !== t32) {
    t33 = {
      mutationKey: t28,
      retry: false,
      mutationFn: t29,
      onMutate: t30,
      onSuccess: t31,
      onError: t32
    };
    $[89] = t28;
    $[90] = t29;
    $[91] = t30;
    $[92] = t31;
    $[93] = t32;
    $[94] = t33;
  } else {
    t33 = $[94];
  }
  const interrupt = useMutation(t33);
  let t34;
  if ($[95] !== sessionId) {
    t34 = codeSessionKeys.mutation(sessionId, "answer");
    $[95] = sessionId;
    $[96] = t34;
  } else {
    t34 = $[96];
  }
  let t35;
  if ($[97] !== client || $[98] !== controller || $[99] !== sessionId || $[100] !== toastRetry) {
    t35 = t36 => {
      const {
        formId,
        answer
      } = t36;
      return withCodeSessionToast(() => controller.conversation.answer(formId, answer), {
        ...answerToasts(formId, answer),
        description: codeSessionActionDescription(client, sessionId),
        onError: toastRetry
      });
    };
    $[97] = client;
    $[98] = controller;
    $[99] = sessionId;
    $[100] = toastRetry;
    $[101] = t35;
  } else {
    t35 = $[101];
  }
  let t36;
  let t37;
  let t38;
  if ($[102] !== controller) {
    t36 = t39 => {
      const {
        formId: formId_0,
        answer: answer_0
      } = t39;
      return controller.beginAnswer(formId_0, answer_0);
    };
    t37 = (_data_1, t40, requestId_0) => {
      const {
        formId: formId_1
      } = t40;
      controller.finishAnswer(formId_1, requestId_0);
      controller.refresh();
    };
    t38 = (error_7, t41, requestId_1) => {
      const {
        formId: formId_2
      } = t41;
      if (requestId_1) {
        controller.finishAnswer(formId_2, requestId_1, error_7 instanceof Error ? error_7.message : "Failed to send answer");
      }
      controller.refresh();
    };
    $[102] = controller;
    $[103] = t36;
    $[104] = t37;
    $[105] = t38;
  } else {
    t36 = $[103];
    t37 = $[104];
    t38 = $[105];
  }
  let t39;
  if ($[106] !== t34 || $[107] !== t35 || $[108] !== t36 || $[109] !== t37 || $[110] !== t38) {
    t39 = {
      mutationKey: t34,
      retry: false,
      mutationFn: t35,
      onMutate: t36,
      onSuccess: t37,
      onError: t38
    };
    $[106] = t34;
    $[107] = t35;
    $[108] = t36;
    $[109] = t37;
    $[110] = t38;
    $[111] = t39;
  } else {
    t39 = $[111];
  }
  const answer_1 = useMutation(t39);
  let t40;
  if ($[112] !== answer_1 || $[113] !== cancel || $[114] !== interrupt || $[115] !== prompt_0 || $[116] !== sendNow || $[117] !== steer) {
    t40 = {
      prompt: prompt_0,
      cancel,
      steer,
      sendNow,
      interrupt,
      answer: answer_1
    };
    $[112] = answer_1;
    $[113] = cancel;
    $[114] = interrupt;
    $[115] = prompt_0;
    $[116] = sendNow;
    $[117] = steer;
    $[118] = t40;
  } else {
    t40 = $[118];
  }
  return t40;
}
export type CodeSessionRewindAction = {
  type: 'stage';
  turnId: string;
  restoreFiles: boolean;
} | {
  type: 'clear';
};
const REWIND_ERROR_TITLE: Record<CodeSessionRewindAction['type'], string> = {
  stage: 'Could not rewind conversation',
  clear: 'Could not cancel the edit'
};

/**
 * Stage or clear a rewind; the next prompt commits it. Like a rename, the
 * request in flight is the optimistic state (`usePendingRewind`): a snapshot
 * read meanwhile cannot undo it, success writes the saved row before the
 * overlay ends, and failure drops it.
 */
export function useRewindMutations(sessionId) {
  const $ = _c(21);
  const client = useQueryClient();
  let t0;
  if ($[0] !== client || $[1] !== sessionId) {
    t0 = getCodeSessionController(client, sessionId);
    $[0] = client;
    $[1] = sessionId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const controller = t0;
  let t1;
  if ($[3] !== sessionId) {
    t1 = codeSessionKeys.mutation(sessionId, "rewind");
    $[3] = sessionId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== controller) {
    t2 = action => {
      switch (action.type) {
        case "stage":
          {
            return controller.conversation.stageRewind(action.turnId, action.restoreFiles);
          }
        case "clear":
          {
            return controller.conversation.clearRewind();
          }
        default:
          {
            const _exhaustive = action;
            return _exhaustive;
          }
      }
    };
    $[5] = controller;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== client || $[8] !== sessionId) {
    t3 = async t4 => {
      const {
        session
      } = t4;
      await client.cancelQueries({
        queryKey: codeSessionKeys.detail(sessionId),
        exact: true
      });
      client.setQueryData(codeSessionKeys.detail(sessionId), data => data ? {
        ...data,
        session
      } : data);
      await listsFor(sessionId).update(client, session);
    };
    $[7] = client;
    $[8] = sessionId;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== client || $[11] !== controller || $[12] !== sessionId) {
    t4 = (error, action_0) => {
      const detail = client.getQueryData(codeSessionKeys.detail(sessionId));
      const turn = action_0.type === "stage" ? detail?.session.turns.find(saved => saved.id === action_0.turnId) : undefined;
      const guidance = action_0.type === "clear" ? "\nThe edit may still be open. Cancel it before sending to keep your earlier messages." : "";
      toast.error(REWIND_ERROR_TITLE[action_0.type], {
        description: `${codeSessionActionDescription(client, sessionId, turn && turnPromptText(turn))}\n${error.message}${guidance}`,
        descriptionClassName: "whitespace-pre-line"
      });
      controller.refresh();
    };
    $[10] = client;
    $[11] = controller;
    $[12] = sessionId;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = {
      mutationKey: t1,
      retry: false,
      mutationFn: t2,
      onSuccess: t3,
      onError: t4
    };
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const {
    mutateAsync
  } = useMutation(t5);
  let t6;
  if ($[19] !== mutateAsync) {
    t6 = {
      stage: (turnId, t7) => {
        const {
          restoreFiles: t8
        } = t7 === undefined ? {} : t7;
        const restoreFiles = t8 === undefined ? true : t8;
        return mutateAsync({
          type: "stage",
          turnId,
          restoreFiles
        });
      },
      clear: () => mutateAsync({
        type: "clear"
      })
    };
    $[19] = mutateAsync;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
}
export function useCancelCheckIn(sessionId) {
  const $ = _c(17);
  const client = useQueryClient();
  let t0;
  if ($[0] !== client || $[1] !== sessionId) {
    t0 = getCodeSessionController(client, sessionId);
    $[0] = client;
    $[1] = sessionId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const controller = t0;
  let t1;
  if ($[3] !== sessionId) {
    t1 = codeSessionKeys.mutation(sessionId, "check-in");
    $[3] = sessionId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== sessionId) {
    t2 = () => codeSessionRequest(`/api/code-sessions/${sessionId}/check-in`, {
      method: "DELETE"
    });
    $[5] = sessionId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== client || $[8] !== sessionId) {
    t3 = async t4 => {
      const {
        session
      } = t4;
      await client.cancelQueries({
        queryKey: codeSessionKeys.detail(sessionId),
        exact: true
      });
      client.setQueryData(codeSessionKeys.detail(sessionId), data => data ? {
        ...data,
        session
      } : data);
    };
    $[7] = client;
    $[8] = sessionId;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== controller) {
    t4 = error => {
      toast.error("Could not cancel the check-in", {
        description: error.message
      });
      controller.refresh();
    };
    $[10] = controller;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = {
      mutationKey: t1,
      retry: false,
      mutationFn: t2,
      onSuccess: t3,
      onError: t4
    };
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return useMutation(t5);
}
export function useSetCodeSessionSpeed(sessionId) {
  const $ = _c(17);
  const client = useQueryClient();
  let t0;
  if ($[0] !== client || $[1] !== sessionId) {
    t0 = getCodeSessionController(client, sessionId);
    $[0] = client;
    $[1] = sessionId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const controller = t0;
  let t1;
  if ($[3] !== sessionId) {
    t1 = codeSessionKeys.mutation(sessionId, "speed");
    $[3] = sessionId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== sessionId) {
    t2 = speed => codeSessionRequest(`/api/code-sessions/${sessionId}/speed`, {
      ...jsonBody({
        speed
      }),
      method: "PUT"
    });
    $[5] = sessionId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== client || $[8] !== sessionId) {
    t3 = async t4 => {
      const {
        session
      } = t4;
      await client.cancelQueries({
        queryKey: codeSessionKeys.detail(sessionId),
        exact: true
      });
      client.setQueryData(codeSessionKeys.detail(sessionId), data => data ? {
        ...data,
        session: {
          ...data.session,
          model: session.model
        }
      } : data);
    };
    $[7] = client;
    $[8] = sessionId;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== controller) {
    t4 = error => {
      toast.error("Could not change Fast mode", {
        description: error.message
      });
      controller.refresh();
    };
    $[10] = controller;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = {
      mutationKey: t1,
      retry: false,
      mutationFn: t2,
      onSuccess: t3,
      onError: t4
    };
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return useMutation(t5);
}
export function useSetCodeSessionApprovalMode(sessionId) {
  const $ = _c(17);
  const client = useQueryClient();
  let t0;
  if ($[0] !== client || $[1] !== sessionId) {
    t0 = getCodeSessionController(client, sessionId);
    $[0] = client;
    $[1] = sessionId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const controller = t0;
  let t1;
  if ($[3] !== sessionId) {
    t1 = codeSessionKeys.mutation(sessionId, "approval-mode");
    $[3] = sessionId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== sessionId) {
    t2 = mode => runtimeFor(sessionId).record.setApprovalMode(mode);
    $[5] = sessionId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== client || $[8] !== sessionId) {
    t3 = async (_, mode_0) => {
      await client.cancelQueries({
        queryKey: codeSessionKeys.detail(sessionId),
        exact: true
      });
      client.setQueryData(codeSessionKeys.detail(sessionId), data => data ? {
        ...data,
        session: {
          ...data.session,
          approvalMode: mode_0
        }
      } : data);
    };
    $[7] = client;
    $[8] = sessionId;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== controller) {
    t4 = error => {
      toast.error("Could not change the approval mode", {
        description: error.message
      });
      controller.refresh();
    };
    $[10] = controller;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = {
      mutationKey: t1,
      retry: false,
      mutationFn: t2,
      onSuccess: t3,
      onError: t4
    };
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return useMutation(t5);
}

/** The rewind request in flight, shared by every view of the session. */
export function usePendingRewind(sessionId) {
  const $ = _c(6);
  let t0;
  if ($[0] !== sessionId) {
    t0 = codeSessionKeys.mutation(sessionId, "rewind");
    $[0] = sessionId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      filters: {
        mutationKey: t0,
        status: "pending"
      },
      select: _temp
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = useMutationState(t1);
  let t3;
  if ($[4] !== t2) {
    t3 = t2.at(-1);
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function _temp(mutation) {
  return mutation.state.variables as CodeSessionRewindAction;
}
export function useSteeringTurnIds(sessionId) {
  const $ = _c(4);
  let t0;
  if ($[0] !== sessionId) {
    t0 = codeSessionKeys.mutation(sessionId, "steer");
    $[0] = sessionId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      filters: {
        mutationKey: t0,
        status: "pending"
      },
      select: _temp2
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return useMutationState(t1);
}
function _temp2(mutation) {
  return mutation.state.variables as string;
}
export function usePendingSessionArchives() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      filters: {
        mutationKey: codeSessionKeys.archive,
        status: "pending"
      },
      select: _temp3
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutationState(t0);
}
function _temp3(mutation) {
  return mutation.state.variables as string;
}
type UnarchiveCodeSessionInput = {
  sessionId: string;
  /** An Undo right after Archive waits for that archive to land first. */
  archiveSettled?: Promise<void>;
};
export function usePendingSessionRestores() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      filters: {
        mutationKey: codeSessionKeys.unarchive,
        status: "pending"
      },
      select: _temp4
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutationState(t0);
}
function _temp4(mutation) {
  return (mutation.state.variables as UnarchiveCodeSessionInput).sessionId;
}
type RenameCodeSessionInput = {
  sessionId: string;
  title: string;
};

/**
 * Titles shown while a rename is in flight, newest rename winning. Success
 * writes the caches before the mutation settles, so the overlay hands off
 * without flashing the old title; failure drops it and the cached title returns.
 */
export function usePendingSessionTitles() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      filters: {
        mutationKey: codeSessionKeys.rename,
        status: "pending"
      },
      select: _temp5
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const renames = useMutationState(t0);
  let t1;
  if ($[1] !== renames) {
    t1 = new Map(renames.map(_temp6));
    $[1] = renames;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/** Run the repository's setup script again in the session's sandbox; the setup row reports it. */
function _temp6(t0) {
  const {
    sessionId,
    title
  } = t0;
  return [sessionId, title];
}
function _temp5(mutation) {
  return mutation.state.variables as RenameCodeSessionInput;
}
export function useRerunCodeSessionSetup(sessionId) {
  const $ = _c(2);
  let t0;
  if ($[0] !== sessionId) {
    t0 = {
      retry: false,
      mutationFn: () => codeSessionRequest(`/api/code-sessions/${sessionId}/setup`, {
        method: "POST"
      }),
      onError: _temp7
    };
    $[0] = sessionId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

/**
 * Writes a saved title into the session's view and into each cached list that holds it, after
 * cancelling their reads under way, which began before the save and could bring the old title
 * back. The new version also keeps out a slower read that still serves the old row, and an
 * earlier save whose answer arrives after a later one's.
 */
function _temp7(error) {
  toast.error("Could not run the setup script", {
    description: error.message
  });
}
async function writeSessionTitle(client: QueryClient, sessionId: string, {
  title,
  updatedAt
}: SavedSessionTitle) {
  const detail = codeSessionKeys.detail(sessionId);
  await client.cancelQueries({
    queryKey: detail,
    exact: true
  });
  client.setQueryData<SessionResponse>(detail, data => data && data.session.updatedAt <= updatedAt ? {
    ...data,
    session: {
      ...data.session,
      title,
      updatedAt
    }
  } : data);
  await listsFor(sessionId).retitle(client, sessionId, {
    title,
    updatedAt
  });
}
export function useRenameCodeSession() {
  const $ = _c(2);
  const client = useQueryClient();
  let t0;
  if ($[0] !== client) {
    t0 = {
      mutationKey: codeSessionKeys.rename,
      retry: false,
      mutationFn: _temp8,
      onSuccess: (saved, t1) => {
        const {
          sessionId: sessionId_0
        } = t1;
        return writeSessionTitle(client, sessionId_0, saved);
      },
      onError: (error, t2) => {
        const {
          sessionId: sessionId_1
        } = t2;
        toast.error("Could not rename session", {
          description: `${codeSessionActionDescription(client, sessionId_1)}\n${error.message}`,
          descriptionClassName: "whitespace-pre-line"
        });
      }
    };
    $[0] = client;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

/** Name the session again from its first message; the model takes a few seconds. */
function _temp8(t0) {
  const {
    sessionId,
    title
  } = t0;
  return runtimeFor(sessionId).record.rename(title);
}
export function useRegenerateCodeSessionTitle() {
  const $ = _c(2);
  const client = useQueryClient();
  let t0;
  if ($[0] !== client) {
    t0 = {
      mutationKey: codeSessionKeys.regenerateTitle,
      retry: false,
      mutationFn: _temp9,
      onSuccess: (saved, sessionId_0) => writeSessionTitle(client, sessionId_0, saved),
      onError: (error, sessionId_1) => {
        toast.error("Could not regenerate title", {
          description: `${codeSessionActionDescription(client, sessionId_1)}\n${error.message}`,
          descriptionClassName: "whitespace-pre-line"
        });
      }
    };
    $[0] = client;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

/** Sessions whose title is being generated again. */
function _temp9(sessionId) {
  return runtimeFor(sessionId).record.regenerateTitle();
}
export function useRegeneratingTitleSessionIds() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      filters: {
        mutationKey: codeSessionKeys.regenerateTitle,
        status: "pending"
      },
      select: _temp10
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const sessionIds = useMutationState(t0);
  let t1;
  if ($[1] !== sessionIds) {
    t1 = new Set(sessionIds);
    $[1] = sessionIds;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function _temp10(mutation) {
  return mutation.state.variables as string;
}
export function useUnarchiveCodeSession() {
  const $ = _c(4);
  const client = useQueryClient();
  let t0;
  if ($[0] !== client) {
    t0 = async session => {
      await client.cancelQueries({
        queryKey: codeSessionKeys.detail(session.id),
        exact: true
      });
      client.setQueryData(codeSessionKeys.detail(session.id), data => data ? {
        ...data,
        session: {
          ...data.session,
          status: session.status,
          sandboxStatus: session.sandboxStatus,
          checkInDueAt: session.checkInDueAt,
          updatedAt: session.updatedAt
        }
      } : undefined);
      await listsFor(session.id).unarchived(client, session);
    };
    $[0] = client;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== client) {
    t1 = async (_data, _error, t2) => {
      const {
        sessionId: sessionId_0
      } = t2;
      client.invalidateQueries({
        queryKey: codeSessionKeys.detail(sessionId_0),
        exact: true
      });
      await listsFor(sessionId_0).settle(client);
    };
    $[2] = client;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const restore = useMutation({
    mutationKey: codeSessionKeys.unarchive,
    retry: false,
    mutationFn: _temp11,
    onSuccess: t0,
    onError: (error, input) => {
      toast.error("Could not unarchive session", {
        description: `${codeSessionActionDescription(client, input.sessionId)}\n${error.message}`,
        descriptionClassName: "whitespace-pre-line",
        action: {
          label: "Retry",
          onClick: () => restore.mutate(input)
        }
      });
    },
    onSettled: t1
  });
  return restore;
}

/** Archive interrupts running and queued work; Undo restores the session without it. */
async function _temp11(t0) {
  const {
    sessionId,
    archiveSettled
  } = t0;
  await archiveSettled;
  return runtimeFor(sessionId).record.unarchive();
}
function hasLiveWork(client: QueryClient, sessionId: string) {
  const detail = client.getQueryData<SessionResponse>(codeSessionKeys.detail(sessionId))?.session;
  const status = (detail ?? listsFor(sessionId).find(client, sessionId))?.status;
  return status === 'creating' || status === 'running' || Boolean(detail?.turns.some(turn => isLiveTurnStatus(turn.status)));
}
export function useArchiveCodeSession() {
  const $ = _c(7);
  const client = useQueryClient();
  const {
    mutate: restore
  } = useUnarchiveCodeSession();
  let t0;
  if ($[0] !== client || $[1] !== restore) {
    t0 = sessionId_0 => {
      let finishArchive;
      const archiveSettled = new Promise(resolve => {
        finishArchive = resolve;
      });
      const operation = {
        undoRequested: false,
        finishArchive
      };
      toast.success("Session archived", {
        id: `code-session-archive:${sessionId_0}`,
        description: [codeSessionActionDescription(client, sessionId_0), ...(hasLiveWork(client, sessionId_0) ? ["Its running work was stopped and will not resume if you undo."] : [])].join("\n"),
        descriptionClassName: "whitespace-pre-line",
        duration: 10000,
        action: {
          label: "Undo",
          onClick: () => {
            if (operation.undoRequested) {
              return;
            }
            operation.undoRequested = true;
            restore({
              sessionId: sessionId_0,
              archiveSettled
            });
          }
        }
      });
      return operation;
    };
    $[0] = client;
    $[1] = restore;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== client) {
    t1 = async session => {
      await client.cancelQueries({
        queryKey: codeSessionKeys.detail(session.id),
        exact: true
      });
      client.setQueryData(codeSessionKeys.detail(session.id), data => data ? {
        ...data,
        session: {
          ...data.session,
          status: "archived",
          sandboxStatus: session.sandboxStatus,
          checkInDueAt: session.checkInDueAt,
          updatedAt: session.updatedAt,
          turns: data.session.turns.map(turn => isLiveTurnStatus(turn.status) ? {
            ...turn,
            status: "interrupted",
            endedAt: turn.endedAt ?? session.updatedAt
          } : turn)
        },
        resuming: false
      } : undefined);
      await listsFor(session.id).archived(client, session);
    };
    $[3] = client;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== client) {
    t2 = async (_data, _error, sessionId_2, operation_1) => {
      operation_1?.finishArchive();
      await listsFor(sessionId_2).settle(client);
    };
    $[5] = client;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const archive = useMutation({
    mutationKey: codeSessionKeys.archive,
    retry: false,
    mutationFn: _temp12,
    onMutate: t0,
    onSuccess: t1,
    onError: (error, sessionId_1, operation_0) => {
      if (operation_0?.undoRequested) {
        return;
      }
      toast.error("Could not archive session", {
        id: `code-session-archive:${sessionId_1}`,
        description: `${codeSessionActionDescription(client, sessionId_1)}\n${error.message}`,
        descriptionClassName: "whitespace-pre-line",
        action: {
          label: "Retry",
          onClick: () => archive.mutate(sessionId_1)
        }
      });
    },
    onSettled: t2
  });
  return archive;
}

/** The session's saved row, or what the sidebar list knows of it. */
function _temp12(sessionId) {
  return runtimeFor(sessionId).record.archive();
}
function savedSession(client: QueryClient, sessionId: string) {
  // The sidebar forks sessions it only read the saved turns of.
  return client.getQueryData<SessionResponse>(codeSessionKeys.detail(sessionId))?.session ?? client.getQueryData<{
    session: CodeSessionRow;
  }>(codeSessionKeys.savedTurns(sessionId))?.session;
}

/**
 * A fork of `sessionId` as the client can show it before the server creates
 * it: the parent's repository under the next fork title. Undefined when
 * nothing about the parent is cached.
 */
function pendingForkRow(client: QueryClient, input: {
  sessionId: string;
  forkId: string;
  viewerProfileId: string;
}): CodeSessionRow | undefined {
  const parent = savedSession(client, input.sessionId);
  const summary = parent ?? listsFor(input.sessionId).find(client, input.sessionId);
  if (!summary) {
    return undefined;
  }
  const siblingTitles = client.getQueriesData<CodeSessionSummary[]>({
    queryKey: codeSessionKeys.lists
  }).flatMap(([, rows]) => rows ?? []).filter(row => row.forkedFromSessionId === input.sessionId).map(row => row.title);
  return {
    id: input.forkId,
    installationId: summary.installationId,
    repositoryId: summary.repositoryId,
    repoFullName: summary.repoFullName,
    baseBranch: parent?.baseBranch ?? '',
    // The server names the fork's branch and sandbox.
    sessionBranch: '',
    sandboxName: '',
    openCodeSessionId: null,
    model: parent?.model ?? '',
    status: 'creating',
    sandboxStatus: 'pending',
    rewindTurnId: null,
    rewindRestoreFiles: true,
    checkInDueAt: null,
    forkedFromSessionId: input.sessionId,
    sideAgentParentId: null,
    startedBySessionId: null,
    // A fork is started here, whatever its parent's source.
    source: 'web',
    sourceUrl: null,
    mcpClientName: null,
    // A fork is its forker's, whoever started its parent.
    createdByProfileId: input.viewerProfileId,
    title: forkedCodeSessionTitle(summary.title, siblingTitles),
    lastActivityAt: new Date().toISOString(),
    awaitingInputAt: null,
    // Older than any saved version, so every server row replaces it.
    updatedAt: new Date(0).toISOString(),
    turns: []
  };
}
type ForkMutationContext = {
  pending?: CodeSessionRow;
};
type ForkCodeSessionInput = {
  sessionId: string;
  /** Fork before this turn; omitted forks from the latest message. */
  beforeTurnId?: string;
} & PreparedFork;

/**
 * Fork a session into a new one that starts with its conversation before a
 * turn, or all of it. Its view can open at once where its runtime names the
 * fork before making it: a pending row stands in until the server's arrives.
 */
export function useForkCodeSession() {
  const $ = _c(12);
  const client = useQueryClient();
  const viewerProfileId = useAuth().user?.id;
  let t0;
  if ($[0] !== client || $[1] !== viewerProfileId) {
    t0 = t1 => {
      const {
        sessionId,
        forkId
      } = t1;
      const pending = forkId === null || viewerProfileId === undefined ? undefined : pendingForkRow(client, {
        sessionId,
        forkId,
        viewerProfileId
      });
      if (pending) {
        client.setQueryData(codeSessionKeys.detail(pending.id), {
          session: pending,
          transcript: null,
          events: [],
          cursor: null,
          resuming: false,
          mcpToolsRunAs: null
        });
      }
      return {
        pending
      };
    };
    $[0] = client;
    $[1] = viewerProfileId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  let t2;
  if ($[3] !== client) {
    t1 = async t3 => {
      const {
        session
      } = t3;
      client.setQueryData(codeSessionKeys.detail(session.id), data => data ? {
        ...data,
        session
      } : data);
      await seedNewSession(client, {
        session
      });
    };
    t2 = (error, t4) => {
      const {
        sessionId: sessionId_0,
        beforeTurnId,
        forkId: forkId_0
      } = t4;
      if (forkId_0 !== null) {
        client.removeQueries({
          queryKey: codeSessionKeys.detail(forkId_0),
          exact: true
        });
      }
      const turn = beforeTurnId === undefined ? undefined : savedSession(client, sessionId_0)?.turns.find(candidate => candidate.id === beforeTurnId);
      toast.error("Could not fork session", {
        description: [codeSessionActionDescription(client, sessionId_0, turn && turnPromptText(turn)), error.message].join("\n"),
        descriptionClassName: "whitespace-pre-line"
      });
    };
    $[3] = client;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      mutationKey: codeSessionKeys.fork,
      retry: false,
      mutationFn: _temp13,
      onMutate: t0,
      onSuccess: t1,
      onError: t2
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    mutateAsync
  } = useMutation(t3);
  let t4;
  if ($[10] !== mutateAsync) {
    t4 = t5 => {
      const {
        sessionId: sessionId_1,
        beforeTurnId: beforeTurnId_0
      } = t5;
      const prepared = runtimeFor(sessionId_1).conversation.fork({
        beforeTurnId: beforeTurnId_0
      });
      return {
        forkId: prepared.forkId,
        forked: mutateAsync({
          sessionId: sessionId_1,
          beforeTurnId: beforeTurnId_0,
          ...prepared
        })
      };
    };
    $[10] = mutateAsync;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}

/** Forks whose request is in flight; their views and sidebar rows already show. */
function _temp13(t0) {
  const {
    make
  } = t0;
  return make();
}
export function usePendingForks() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      filters: {
        mutationKey: codeSessionKeys.fork,
        status: "pending"
      },
      select: _temp14
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutationState(t0);
}

/** True while the session is a fork whose row the server has not created yet. */
function _temp14(mutation) {
  const {
    sessionId,
    beforeTurnId,
    forkId
  } = mutation.state.variables as ForkCodeSessionInput;
  return {
    sessionId,
    beforeTurnId,
    forkId,
    pending: (mutation.state.context as ForkMutationContext | undefined)?.pending
  };
}
export function useForkStarting(sessionId) {
  const $ = _c(5);
  const t0 = usePendingForks();
  let t1;
  if ($[0] !== sessionId || $[1] !== t0) {
    let t2;
    if ($[3] !== sessionId) {
      t2 = fork => fork.forkId === sessionId;
      $[3] = sessionId;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = t0.some(t2);
    $[0] = sessionId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/** The side agent's ID, chosen here so its tab opens at once, and its first request. */
export type CreateSideAgentInput = {
  agentId: string;
  prompt: string;
};

/** A side agent's latest start request, while it runs or after it failed. */
export type SideAgentStart = {
  status: 'pending';
  prompt: string;
  submittedAt: number;
} | {
  status: 'failed';
  prompt: string;
  submittedAt: number;
  error: string;
};
const sideAgentStartKey = (sessionId: string) => codeSessionKeys.mutation(sessionId, 'side-agent');

/** Start a side agent in the session's VM and checkout. */
export function useCreateSideAgent(sessionId) {
  const $ = _c(11);
  const client = useQueryClient();
  let t0;
  if ($[0] !== sessionId) {
    t0 = sideAgentStartKey(sessionId);
    $[0] = sessionId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== sessionId) {
    t1 = input => codeSessionRequest(`/api/code-sessions/${sessionId}/side-agents`, jsonBody(input));
    $[2] = sessionId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== client || $[5] !== sessionId) {
    t2 = () => {
      client.invalidateQueries({
        queryKey: codeSessionKeys.sideAgents(sessionId)
      });
    };
    $[4] = client;
    $[5] = sessionId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t0 || $[8] !== t1 || $[9] !== t2) {
    t3 = {
      mutationKey: t0,
      retry: false,
      gcTime: Infinity,
      mutationFn: t1,
      onSuccess: t2
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return useMutation(t3);
}

/** Each side agent's latest start, by agent ID, while it is pending or failed; a success clears it. */
export function useSideAgentStarts(sessionId) {
  const $ = _c(6);
  let t0;
  if ($[0] !== sessionId) {
    t0 = sideAgentStartKey(sessionId);
    $[0] = sessionId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      filters: {
        mutationKey: t0
      },
      select: _temp15
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const attempts = useMutationState(t1);
  let starts;
  if ($[4] !== attempts) {
    starts = new Map();
    for (const {
      agentId,
      prompt,
      status,
      submittedAt,
      error
    } of attempts) {
      if (status === "pending") {
        starts.set(agentId, {
          status,
          prompt,
          submittedAt
        });
      } else {
        if (status === "error") {
          starts.set(agentId, {
            status: "failed",
            prompt,
            submittedAt,
            error: error || "Could not start the side chat."
          });
        } else {
          starts.delete(agentId);
        }
      }
    }
    $[4] = attempts;
    $[5] = starts;
  } else {
    starts = $[5];
  }
  return starts;
}
function _temp15(t0) {
  const {
    state
  } = t0;
  return {
    ...(state.variables as CreateSideAgentInput),
    status: state.status,
    submittedAt: state.submittedAt,
    error: state.error?.message
  };
}